import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaStar, FaRegStar, FaCheck, FaChartBar, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formsService } from '../../../../services/formsService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { useCurrentForm } from '../../../../hooks/community/useForms';
import { getUserId } from '../../../../services/httpService';
import { FormQuestion, FormQuestionType, SubmitFormAnswerDto } from '../../../../models/forms.model';
import Button from '../../../../components/misc/Button';

export default function CommunityFormDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formSlug } = useParams<{ formSlug: string }>();
  const community = useSelectedCommunity();
  const currentForm = useCurrentForm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<Record<number, SubmitFormAnswerDto>>({});

  const userId = getUserId();
  const isAdmin = community?.role === 'ADMIN';

  // Check if user has already responded
  const existingResponse = useMemo(() => {
    return currentForm?.responses?.find((r) => r.userId === userId);
  }, [currentForm, userId]);

  const isEditing = !!existingResponse;

  const isDeadlinePassed = useMemo(() => {
    if (!currentForm?.deadline) return false;
    return new Date(currentForm.deadline) < new Date();
  }, [currentForm]);

  const canSubmit = !isDeadlinePassed || isEditing;

  useEffect(() => {
    if (!community?.id || !formSlug) return;

    setLoading(true);
    formsService
      .getFormBySlug(community.id, formSlug)
      .catch((error) => {
        console.error('Failed to load form:', error);
        toast.error(t('error.loadFailed'));
        navigate('/community/forms');
      })
      .finally(() => setLoading(false));
  }, [community?.id, formSlug]);

  // Initialize answers from existing response if editing
  useEffect(() => {
    if (existingResponse && currentForm) {
      const initialAnswers: Record<number, SubmitFormAnswerDto> = {};

      currentForm.questions.forEach((question) => {
        const questionAnswers = existingResponse.answers.filter((a) => a.questionId === question.formQuestionId);

        if (question.type === FormQuestionType.CHOICE) {
          const maxAnswers = question.meta?.maxAnswers || 1;
          if (maxAnswers > 1) {
            // Multi-select
            const optionIds = questionAnswers.map((a) => a.optionId).filter(Boolean) as number[];
            initialAnswers[question.formQuestionId] = {
              questionId: question.formQuestionId,
              optionIds,
            };
          } else {
            // Single-select
            const answer = questionAnswers[0];
            initialAnswers[question.formQuestionId] = {
              questionId: question.formQuestionId,
              optionId: answer?.optionId || null,
            };
          }
        } else {
          const answer = questionAnswers[0];
          initialAnswers[question.formQuestionId] = {
            questionId: question.formQuestionId,
            response: answer?.text || '',
          };
        }
      });

      setAnswers(initialAnswers);
    }
  }, [existingResponse, currentForm]);

  const handleBack = () => {
    navigate('/community/forms');
  };

  const handleViewResults = () => {
    navigate(`/community/forms/${formSlug}/results`);
  };

  const handleTextChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { questionId, response: value },
    }));
  };

  const handleChoiceSingle = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { questionId, optionId },
    }));
  };

  const handleChoiceMultiple = (questionId: number, optionId: number, maxAnswers: number) => {
    setAnswers((prev) => {
      const current = prev[questionId]?.optionIds || [];
      let newOptionIds: number[];

      if (current.includes(optionId)) {
        newOptionIds = current.filter((id) => id !== optionId);
      } else {
        if (current.length >= maxAnswers) {
          toast.warning(t('page.forms.maxAnswersReached', { max: maxAnswers }));
          return prev;
        }
        newOptionIds = [...current, optionId];
      }

      return {
        ...prev,
        [questionId]: { questionId, optionIds: newOptionIds },
      };
    });
  };

  const handleRatingChange = (questionId: number, rating: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { questionId, response: String(rating) },
    }));
  };

  const validateAnswers = (): boolean => {
    if (!currentForm) return false;

    const invalidQuestion = currentForm.questions.find((question) => {
      if (!question.mandatory) return false;

      const answer = answers[question.formQuestionId];

      if (!answer) return true;

      switch (question.type) {
        case FormQuestionType.CHOICE: {
          const maxAnswers = question.meta?.maxAnswers || 1;
          if (maxAnswers > 1) {
            return !answer.optionIds || answer.optionIds.length === 0;
          }
          return !answer.optionId;
        }
        case FormQuestionType.TEXT:
        case FormQuestionType.NUMBER:
        case FormQuestionType.DATE:
        case FormQuestionType.RATING:
          return !answer.response || String(answer.response).trim() === '';
        default:
          return false;
      }
    });

    if (invalidQuestion) {
      toast.error(t('page.forms.answerRequired', { question: invalidQuestion.label }));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!community?.id || !formSlug || !currentForm) return;

    if (!validateAnswers()) return;

    setSubmitting(true);

    const answersArray: SubmitFormAnswerDto[] = currentForm.questions.map((q) => {
      const answer = answers[q.formQuestionId];
      return {
        questionId: q.formQuestionId,
        response: answer?.response || null,
        optionId: answer?.optionId || null,
        optionIds: answer?.optionIds || null,
      };
    });

    try {
      if (isEditing) {
        await formsService.updateFormResponse(community.id, formSlug, {
          userId,
          answers: answersArray,
        });
        toast.success(t('page.forms.updateSuccess'));
      } else {
        await formsService.submitFormResponse(community.id, formSlug, {
          userId,
          answers: answersArray,
        });
        toast.success(t('page.forms.submitSuccess'));
      }

      // Refresh the form to get updated responses
      await formsService.getFormBySlug(community.id, formSlug);
    } catch (error: any) {
      console.error('Failed to submit form:', error);
      if (error.response?.data?.error === 'DEADLINE_PASSED') {
        toast.error(t('page.forms.errors.deadlinePassed'));
      } else if (error.response?.data?.error === 'ALREADY_RESPONDED') {
        toast.error(t('page.forms.errors.alreadyResponded'));
      } else {
        toast.error(t('page.forms.errors.submitFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: FormQuestion, index: number) => {
    const answer = answers[question.formQuestionId];

    return (
      <div key={question.formQuestionId} className="rounded-lg border border-secondary-200">
        <div className="mb-3 flex items-start gap-2 rounded-t-lg bg-secondary-200 p-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
            {index + 1}
          </span>
          <div className="flex-1">
            <p className="font-medium text-gray-900">
              {question.label}
              {question.mandatory && <span className="ml-1 text-red-500">*</span>}
            </p>
          </div>
        </div>

        <div className="p-2">
          {question.type === FormQuestionType.TEXT && (
            <textarea
              value={answer?.response || ''}
              onChange={(e) => handleTextChange(question.formQuestionId, e.target.value)}
              placeholder={t('page.forms.placeholder.text')}
              disabled={isDeadlinePassed && !isEditing}
              className="w-full rounded-lg border p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100"
              rows={3}
            />
          )}

          {question.type === FormQuestionType.NUMBER && (
            <input
              type="number"
              value={answer?.response || ''}
              onChange={(e) => handleTextChange(question.formQuestionId, e.target.value)}
              placeholder={t('page.forms.placeholder.number')}
              disabled={isDeadlinePassed && !isEditing}
              min={question.meta?.min}
              max={question.meta?.max}
              className="w-full rounded-lg border !bg-secondary-50 p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100"
            />
          )}

          {question.type === FormQuestionType.DATE && (
            <input
              type="date"
              value={answer?.response || ''}
              onChange={(e) => handleTextChange(question.formQuestionId, e.target.value)}
              disabled={isDeadlinePassed && !isEditing}
              className="w-full rounded-lg border p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100"
            />
          )}

          {question.type === FormQuestionType.RATING && (
            <div className="flex gap-1">
              {Array.from({ length: question.meta?.scale || 5 }, (_, i) => i + 1).map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingChange(question.formQuestionId, star)}
                  disabled={isDeadlinePassed && !isEditing}
                  className="p-1 transition-transform hover:scale-110 disabled:opacity-50">
                  {Number(answer?.response) >= star ? (
                    <FaStar className="h-8 w-8 text-yellow-400" />
                  ) : (
                    <FaRegStar className="h-8 w-8 text-gray-300" />
                  )}
                </button>
              ))}
            </div>
          )}

          {question.type === FormQuestionType.CHOICE && question.options && (
            <div className="flex flex-col gap-2">
              {(question.meta?.maxAnswers || 1) > 1 && (
                <p className="text-xs text-gray-500">
                  {t('page.forms.selectUpTo', { max: question.meta?.maxAnswers })}
                </p>
              )}
              {question.options.map((option) => {
                const maxAnswers = question.meta?.maxAnswers || 1;
                const isMultiple = maxAnswers > 1;
                const isSelected = isMultiple
                  ? answer?.optionIds?.includes(option.formOptionId)
                  : answer?.optionId === option.formOptionId;

                return (
                  <button
                    key={option.formOptionId}
                    type="button"
                    onClick={() =>
                      isMultiple
                        ? handleChoiceMultiple(question.formQuestionId, option.formOptionId, maxAnswers)
                        : handleChoiceSingle(question.formQuestionId, option.formOptionId)
                    }
                    disabled={isDeadlinePassed && !isEditing}
                    className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                      isSelected ? 'border-primary bg-primary/5 text-primary' : 'hover:border-primary hover:bg-gray-50'
                    } disabled:opacity-50`}>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected && 'border-primary bg-primary'
                      }`}>
                      {isSelected && <FaCheck className="h-3 w-3 text-white" />}
                    </span>
                    <span className="text-sm">{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-secondary-600 text-lg">{t('page.forms.loading')}</div>
      </div>
    );
  }

  if (!currentForm) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-gray-600">{t('page.forms.notFound')}</p>
        <button
          type="button"
          onClick={handleBack}
          className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-opacity-90">
          {t('general.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/community/forms')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary">
          <FaArrowLeft className="h-4 w-4" />
          {t('page.forms.backToForms')}
        </button>

        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="primary-outline" onClick={() => navigate(`/community/forms/${formSlug}/edit`)}>
              <FaEdit className="h-4 w-4" />
              {t('general.edit')}
            </Button>
          )}
          {currentForm.resultsVisibility === 'PUBLIC' && (
            <Button variant="primary" onClick={handleViewResults}>
              <FaChartBar className="h-4 w-4" />
              {t('page.forms.viewResults')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex w-full max-w-4xl flex-col gap-4">
        {/* Form Title & Description */}
        <div className="rounded-xl border border-secondary-200">
          <div className="flex rounded-t-xl bg-secondary-200 p-2">
            <h1 className="text-2xl font-bold text-primary">{currentForm.title}</h1>
          </div>
          <div className="p-2">
            {currentForm.description && <p className="mt-2 text-gray-600">{currentForm.description}</p>}
            {currentForm.deadline && (
              <p className="mt-2 text-sm text-gray-500">
                {t('page.forms.deadline')}:{' '}
                {new Date(currentForm.deadline).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {isDeadlinePassed && <span className="ml-2 text-red-500">({t('page.forms.deadlinePassed')})</span>}
              </p>
            )}
            {isEditing && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 p-2 text-sm text-green-700">
                <FaCheck className="h-4 w-4" />
                {t('page.forms.alreadyResponded')}
              </div>
            )}
          </div>
        </div>

        {/* Questions */}
        <div className="flex flex-col gap-4">
          {currentForm.questions && currentForm.questions.length > 0 ? (
            currentForm.questions.map((question, index) => renderQuestion(question, index))
          ) : (
            <div className="rounded-xl bg-secondary-50 p-4 text-center text-gray-500">
              {t('page.forms.noQuestions')}
            </div>
          )}
        </div>

        {/* Submit Button */}
        {canSubmit && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-lg bg-primary px-6 py-3 font-medium text-white transition-colors hover:bg-opacity-90 disabled:opacity-50">
              {submitting
                ? t('page.forms.submitting')
                : isEditing
                  ? t('page.forms.updateResponse')
                  : t('page.forms.submitResponse')}
            </button>
          </div>
        )}

        {!canSubmit && !isEditing && (
          <div className="rounded-lg bg-red-50 p-4 text-center text-red-700">{t('page.forms.formClosed')}</div>
        )}
      </div>
    </div>
  );
}
